import { z } from 'zod'

export const jsonApiMediaType = 'application/vnd.api+json'

const jsonApiResourceObjectSchema = z.object({
  type: z.string().min(1),
  id: z.string().min(1),
  attributes: z.record(z.string(), z.unknown()).optional(),
  relationships: z.record(z.string(), z.unknown()).optional(),
  links: z.record(z.string(), z.unknown()).optional(),
  meta: z.record(z.string(), z.unknown()).optional(),
})

export const jsonApiDocumentSchema = z.object({
  data: z
    .union([jsonApiResourceObjectSchema, z.array(jsonApiResourceObjectSchema), z.null()])
    .optional(),
  errors: z
    .array(
      z.object({
        status: z.string().optional(),
        code: z.string().optional(),
        title: z.string().optional(),
        detail: z.string().optional(),
        source: z
          .object({
            pointer: z.string().optional(),
            parameter: z.string().optional(),
          })
          .optional(),
        meta: z.record(z.string(), z.unknown()).optional(),
      }),
    )
    .optional(),
  meta: z.record(z.string(), z.unknown()).optional(),
  links: z.record(z.string(), z.unknown()).optional(),
  included: z.array(jsonApiResourceObjectSchema).optional(),
})

export type JsonApiDocument = z.infer<typeof jsonApiDocumentSchema>
export type JsonApiErrorObject = NonNullable<JsonApiDocument['errors']>[number]

/**
 * Map a JSON Pointer from a flat write body to a React Hook Form field path.
 * `/email` → `email`, `/decisions/0/action` → `decisions.0.action`
 */
export function pointerToField(pointer: string): string {
  const trimmed = pointer.startsWith('/') ? pointer.slice(1) : pointer
  if (trimmed.length === 0) {
    return ''
  }

  return trimmed
    .split('/')
    .map((segment) => segment.replaceAll('~1', '/').replaceAll('~0', '~'))
    .join('.')
}

export function parseResource<TAttributes extends z.ZodTypeAny>(
  doc: unknown,
  type: string,
  attributesSchema: TAttributes,
): { id: string } & z.infer<TAttributes> {
  const document = jsonApiDocumentSchema.parse(doc)
  const data = document.data

  if (data === undefined || data === null || Array.isArray(data)) {
    throw new z.ZodError([
      {
        code: 'custom',
        path: ['data'],
        message: 'Expected a single JSON:API resource object',
      },
    ])
  }

  if (data.type !== type) {
    throw new z.ZodError([
      {
        code: 'custom',
        path: ['data', 'type'],
        message: `Expected type "${type}", received "${data.type}"`,
      },
    ])
  }

  const attributes = asAttributeRecord(attributesSchema.parse(data.attributes ?? {}))

  return {
    id: data.id,
    ...attributes,
  } as { id: string } & z.infer<TAttributes>
}

export function parseCollection<TAttributes extends z.ZodTypeAny>(
  doc: unknown,
  type: string,
  attributesSchema: TAttributes,
): Array<{ id: string } & z.infer<TAttributes>> {
  const document = jsonApiDocumentSchema.parse(doc)
  const data = document.data

  if (!Array.isArray(data)) {
    throw new z.ZodError([
      {
        code: 'custom',
        path: ['data'],
        message: 'Expected a JSON:API resource collection',
      },
    ])
  }

  return data.map((item, index) => {
    if (item.type !== type) {
      throw new z.ZodError([
        {
          code: 'custom',
          path: ['data', index, 'type'],
          message: `Expected type "${type}", received "${item.type}"`,
        },
      ])
    }

    const attributes = asAttributeRecord(attributesSchema.parse(item.attributes ?? {}))

    return {
      id: item.id,
      ...attributes,
    } as { id: string } & z.infer<TAttributes>
  })
}

function asAttributeRecord(value: unknown): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new z.ZodError([
      {
        code: 'custom',
        path: ['attributes'],
        message: 'Expected object attributes',
      },
    ])
  }

  return value as Record<string, unknown>
}
